import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';

import {
  contentBlockTypes,
  contentFieldsCreate,
  contentFieldsImpact,
  contentFieldsUpdate,
} from '@/shared/api/generated/content/content';
import type {
  Condition,
  ConditionOp,
  FieldBody,
  FieldTypeBody,
  ImpactOutputBody,
  Rules,
} from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Switch } from '@/shared/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import styles from './FieldFormDialog.module.scss';
import { keyify } from './keyify';

export interface FieldFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  typeID: string;
  /** Вид типа: внутри блока не бывает поля «блоки». */
  typeKind: string;
  /** Заполнено — правим существующее, пусто — заводим новое. */
  field?: FieldBody | undefined;
  /** Группа, в которую добавляется поле. */
  parent?: FieldBody | undefined;
  /** Поля того же уровня — для условия показа. */
  siblings: FieldBody[];
  /** Вкладки, которые уже есть у типа, — чтобы не плодить «Цена» и «цена». */
  tabs: string[];
  fieldTypes: FieldTypeBody[];
  onDone: () => void;
}

/** Форма поля: одна на создание и на правку. */
export function FieldFormDialog({ open, onOpenChange, ...rest }: FieldFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <Body onOpenChange={onOpenChange} {...rest} />}
    </Dialog>
  );
}

type Section = 'main' | 'rules' | 'place';

/** «Любая коллекция» в выпадающем списке: пустое значение Select не принимает. */
const ANY_COLLECTION = '*';

const OPS: { value: ConditionOp; label: string }[] = [
  { value: 'equals', label: 'равно' },
  { value: 'not_equals', label: 'не равно' },
  { value: 'filled', label: 'заполнено' },
  { value: 'empty', label: 'пусто' },
];

function Body({
  typeID,
  typeKind,
  field,
  parent,
  siblings,
  tabs,
  fieldTypes,
  onOpenChange,
  onDone,
}: Omit<FieldFormDialogProps, 'open'>) {
  const editing = field !== undefined;

  const [section, setSection] = useState<Section>('main');
  const [name, setName] = useState(field?.name ?? '');
  const [key, setKey] = useState(field?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(editing);
  const [type, setType] = useState(field?.type ?? 'string');
  const [widget, setWidget] = useState(field?.widget ?? '');
  const [required, setRequired] = useState(field?.required ?? false);
  const [hint, setHint] = useState(field?.hint ?? '');
  const [choices, setChoices] = useState(choicesText(field));
  const [rules, setRules] = useState<Rules>(field?.rules ?? {});
  const [condition, setCondition] = useState<Condition | undefined>(field?.show_if);
  const [width, setWidth] = useState<'full' | 'half'>(field?.width ?? 'full');
  const [tab, setTab] = useState(field?.tab ?? '');
  const [translatable, setTranslatable] = useState(field?.translatable ?? false);
  const [privateField, setPrivateField] = useState(
    (field?.options as Record<string, unknown> | undefined)?.['private'] === true,
  );
  const [multiple, setMultiple] = useState(
    (field?.options as Record<string, unknown> | undefined)?.['multiple'] === true,
  );
  // Ссылка — из какой коллекции (короткое имя: у дева и прода оно общее).
  const [collection, setCollection] = useState(() => {
    const raw = (field?.options as Record<string, unknown> | undefined)?.['collection'];

    return typeof raw === 'string' ? raw : '';
  });
  const collections = (useSiteScope()?.types ?? []).filter((item) => item.kind === 'collection');
  const [allowedBlocks, setAllowedBlocks] = useState<string[]>(() => {
    const raw = (field?.options as Record<string, unknown> | undefined)?.['allowed_blocks'];

    return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
  });

  // Что новые настройки сделают с уже заведёнными записями. Показывается
  // один раз: второе нажатие — осознанное «сохранить всё равно».
  const [impact, setImpact] = useState<ImpactOutputBody>();
  const [error, setError] = useState<string>();
  const [keyError, setKeyError] = useState<string>();
  const [busy, setBusy] = useState(false);

  // Внутри группы не бывает групп и блоков, внутри блока — блоков.
  const available = fieldTypes.filter(
    (item) =>
      !(parent !== undefined && (item.type === 'group' || item.type === 'blocks')) &&
      !(typeKind === 'block' && item.type === 'blocks'),
  );
  const current = fieldTypes.find((item) => item.type === type);
  const widgets = current?.widgets ?? [];
  const allowed = new Set(current?.rules ?? []);
  const others = siblings.filter((item) => item.id !== field?.id && !item.hidden);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setKeyError(undefined);

    const cleanRules = pruneRules(rules, allowed);

    // Правка, которая может не подойти уже заведённым записям, сначала
    // прогоняется по ним: «стало обязательным — у 120 записей пусто».
    const tightened =
      editing &&
      impact === undefined &&
      ((required && !field.required) ||
        widget !== field.widget ||
        JSON.stringify(cleanRules) !== JSON.stringify(pruneRules(field.rules, allowed)));

    if (tightened) {
      setBusy(true);

      contentFieldsImpact(field.id, { required, rules: cleanRules, widget })
        .then((response) => {
          if (
            response.status === 200 &&
            (response.data.failing > 0 || response.data.duplicates > 0)
          ) {
            setImpact(response.data);
            setBusy(false);

            return;
          }

          save(cleanRules);
        })
        .catch(() => {
          // Подсчёт не удался — не повод не сохранять: сервер всё равно
          // проверит каждую запись при её сохранении.
          save(cleanRules);
        });

      return;
    }

    save(cleanRules);
  };

  const save = (cleanRules: Rules) => {
    setBusy(true);

    const own =
      type === 'select'
        ? { choices: parseChoices(choices) }
        : type === 'blocks'
          ? { allowed_blocks: allowedBlocks }
          : type === 'image' || type === 'file'
            ? { multiple }
            : type === 'reference'
              ? { multiple, ...(collection === '' ? {} : { collection }) }
              : {};
    // Поверх прежних настроек: то, чем это окно не управляет, не теряется.
    const options = {
      ...((field?.options as Record<string, unknown> | undefined) ?? {}),
      ...own,
      private: privateField,
    };

    const request = editing
      ? contentFieldsUpdate(
          field.id,
          {
            name: name.trim(),
            hint,
            required,
            widget,
            rules: cleanRules,
            width,
            tab,
            translatable: translatable && current?.translatable === true,
            options,
            ...(condition === undefined ? { clear_show_if: true } : { show_if: condition }),
          },
          { 'If-Match': field.etag },
        )
      : contentFieldsCreate(typeID, {
          key,
          name: name.trim(),
          type,
          required,
          hint,
          widget,
          rules: cleanRules,
          width,
          tab,
          translatable: translatable && current?.translatable === true,
          options,
          ...(condition === undefined ? {} : { show_if: condition }),
          ...(parent === undefined ? {} : { parent_id: parent.id }),
        });

    request
      .then(() => {
        toast.success(editing ? 'Изменения сохранены' : 'Поле добавлено');
        onDone();
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 409) {
          setSection('main');
          setKeyError(cause.detail);
        } else if (cause instanceof ApiError) {
          setError(cause.detail);
        } else {
          setError('Не удалось сохранить. Попробуйте ещё раз.');
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const title = editing
    ? `Поле «${field.name}»`
    : parent === undefined
      ? 'Новое поле'
      : `Новое поле в группе «${parent.name}»`;

  return (
    <DialogContent
      title={title}
      size="md"
      dismissible={false}
      footer={
        <>
          {error !== undefined && <p className={styles['error']}>{error}</p>}
          <Button
            variant="outline"
            tone="neutral"
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Отмена
          </Button>
          <Button type="submit" form="field-form" loading={busy}>
            {impact !== undefined ? 'Сохранить всё равно' : editing ? 'Сохранить' : 'Добавить'}
          </Button>
        </>
      }
    >
      <form id="field-form" onSubmit={handleSubmit} noValidate>
        {impact !== undefined && <ImpactWarning impact={impact} />}
        <Tabs
          value={section}
          onValueChange={(value) => {
            setSection(value as Section);
          }}
        >
          <TabsList>
            <TabsTrigger value="main" active={section === 'main'}>
              Основное
            </TabsTrigger>
            <TabsTrigger value="rules" active={section === 'rules'}>
              Проверки
            </TabsTrigger>
            <TabsTrigger value="place" active={section === 'place'}>
              Показ и место
            </TabsTrigger>
          </TabsList>

          <TabsContent value="main" className={styles['section']}>
            <Field label="Название" required>
              <Input
                value={name}
                autoFocus
                placeholder="Заголовок"
                onChange={(event) => {
                  setName(event.target.value);

                  if (!keyTouched) {
                    setKey(keyify(event.target.value));
                  }
                }}
              />
            </Field>

            <Field
              label="Ключ"
              required
              error={keyError}
              disabled={editing}
              hint={
                editing
                  ? 'Ключ не меняется: под ним лежат значения во всех уже созданных записях.'
                  : 'Имя значения в API — его видят разработчики сайта. Латиница, выбирается один раз.'
              }
            >
              <Input
                mono
                value={key}
                disabled={editing}
                placeholder="title"
                onChange={(event) => {
                  setKeyTouched(true);
                  setKey(event.target.value);
                }}
              />
            </Field>

            <div className={styles['row']}>
              <Field
                label="Вид"
                disabled={editing}
                hint={editing ? 'Вид меняется отдельно — с предпросмотром последствий.' : undefined}
              >
                <Select
                  value={type}
                  onValueChange={(next) => {
                    setType(next);
                    setWidget('');
                    setRules({});
                  }}
                  disabled={editing}
                >
                  {available.map((item) => (
                    <SelectItem key={item.type} value={item.type}>
                      {item.title}
                    </SelectItem>
                  ))}
                </Select>
              </Field>

              {widgets.length > 0 && (
                <Field
                  label="Редактор"
                  hint={widgets.find((w) => w.value === (widget || widgets[0]?.value))?.hint}
                >
                  <Select value={widget || (widgets[0]?.value ?? '')} onValueChange={setWidget}>
                    {widgets.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </Select>
                </Field>
              )}
            </div>

            {type === 'blocks' && (
              <AllowedBlocks chosen={allowedBlocks} onChange={setAllowedBlocks} />
            )}

            {(type === 'image' || type === 'file') && (
              <Switch checked={multiple} onCheckedChange={setMultiple}>
                {type === 'image'
                  ? 'Несколько картинок — галерея, фото товара'
                  : 'Несколько файлов — вложения, документы'}
              </Switch>
            )}

            {type === 'reference' && (
              <>
                <Field
                  label="Из коллекции"
                  hint="Выбор записи покажет только её — и сайт получит типизированные записи."
                >
                  <Select
                    value={collection === '' ? ANY_COLLECTION : collection}
                    onValueChange={(value) => {
                      setCollection(value === ANY_COLLECTION ? '' : value);
                    }}
                  >
                    <SelectItem value={ANY_COLLECTION}>Любая</SelectItem>
                    {collections.map((item) => (
                      <SelectItem key={item.id} value={item.slug}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </Select>
                </Field>
                <Switch checked={multiple} onCheckedChange={setMultiple}>
                  Несколько записей — категории, подборка на главной
                </Switch>
              </>
            )}

            {type === 'select' && (
              <Field label="Варианты" hint="По одному в строке. Порядок — как в списке.">
                <Textarea
                  rows={4}
                  value={choices}
                  placeholder={'онлайн\nофлайн'}
                  onChange={(event) => {
                    setChoices(event.target.value);
                  }}
                />
              </Field>
            )}

            <Field label="Подсказка" hint="Короткое пояснение под полем в редакторе записи.">
              <Input
                value={hint}
                placeholder="Например: «Не длиннее 80 символов — обрежется в карточке»"
                onChange={(event) => {
                  setHint(event.target.value);
                }}
              />
            </Field>

            <Switch checked={required} onCheckedChange={setRequired}>
              Обязательное — без него запись не опубликовать
            </Switch>

            {current?.translatable === true && parent === undefined && (
              <Switch checked={translatable} onCheckedChange={setTranslatable}>
                Переводимое — у поля свои значения на других языках сайта
              </Switch>
            )}

            <Switch checked={privateField} onCheckedChange={setPrivateField}>
              Скрытое — видно только в админке, сайту через API не уходит никогда: правильный ответ
              теста, внутренний комментарий
            </Switch>
          </TabsContent>

          <TabsContent value="rules" className={styles['section']}>
            <RulesEditor allowed={allowed} rules={rules} widget={widget} onChange={setRules} />
          </TabsContent>

          <TabsContent value="place" className={styles['section']}>
            <ConditionEditor condition={condition} others={others} onChange={setCondition} />

            <Field label="Ширина в форме">
              <Segmented
                value={width}
                onValueChange={(next) => {
                  setWidth(next as 'full' | 'half');
                }}
                aria-label="Ширина в форме"
                options={[
                  { value: 'full', label: 'Во всю строку' },
                  { value: 'half', label: 'Половина — два поля рядом' },
                ]}
              />
            </Field>

            {parent === undefined && (
              <Field
                label="Вкладка"
                hint="Пусто — основная. Поля с одной вкладкой собираются вместе: «SEO», «Цены», «Внешний вид»."
              >
                <Input
                  value={tab}
                  list="field-tabs"
                  maxLength={40}
                  placeholder="Основная"
                  onChange={(event) => {
                    setTab(event.target.value);
                  }}
                />
                <datalist id="field-tabs">
                  {tabs
                    .filter((item) => item !== '')
                    .map((item) => (
                      <option key={item} value={item} />
                    ))}
                </datalist>
              </Field>
            )}
          </TabsContent>
        </Tabs>
      </form>
    </DialogContent>
  );
}

function RulesEditor({
  allowed,
  rules,
  widget,
  onChange,
}: {
  allowed: Set<string>;
  rules: Rules;
  widget: string;
  onChange: (rules: Rules) => void;
}) {
  if (allowed.size === 0) {
    return <p className={styles['muted']}>У этого вида поля нет дополнительных проверок.</p>;
  }

  const set = <K extends keyof Rules>(name: K, value: Rules[K] | undefined) => {
    const next = { ...rules };

    if (value === undefined) {
      delete next[name];
    } else {
      next[name] = value;
    }

    onChange(next);
  };

  const number = (
    label: string,
    value: number | undefined,
    apply: (v: number | undefined) => void,
  ): ReactNode => (
    <Field label={label}>
      <Input
        type="number"
        value={value === undefined ? '' : String(value)}
        onChange={(event) => {
          apply(event.target.value === '' ? undefined : Number(event.target.value));
        }}
      />
    </Field>
  );

  return (
    <>
      {allowed.has('length') && (
        <div className={styles['row']}>
          {number('Не короче, символов', rules.min_length, (v) => {
            set('min_length', v);
          })}
          {number('Не длиннее, символов', rules.max_length, (v) => {
            set('max_length', v);
          })}
        </div>
      )}

      {allowed.has('range') && (
        <div className={styles['row']}>
          {number(
            widget === 'slider' ? 'Наименьшее (нужно ползунку)' : 'Не меньше',
            rules.min,
            (v) => {
              set('min', v);
            },
          )}
          {number(
            widget === 'slider' ? 'Наибольшее (нужно ползунку)' : 'Не больше',
            rules.max,
            (v) => {
              set('max', v);
            },
          )}
        </div>
      )}

      {allowed.has('items') && (
        <div className={styles['row']}>
          {number('Не меньше элементов', rules.min_items, (v) => {
            set('min_items', v);
          })}
          {number('Не больше элементов', rules.max_items, (v) => {
            set('max_items', v);
          })}
        </div>
      )}

      {allowed.has('integer') && (
        <Switch
          checked={rules.integer === true}
          onCheckedChange={(v) => {
            set('integer', v);
          }}
        >
          Только целое число
        </Switch>
      )}

      {allowed.has('pattern') && (
        <>
          <Field
            label="Формат"
            hint="Регулярное выражение. Например, ^[A-Z]{2}-\d+$ — артикул вида AB-123."
          >
            <Input
              mono
              value={rules.pattern ?? ''}
              placeholder="^[A-Z]{2}-\d+$"
              onChange={(event) => {
                set('pattern', event.target.value);
              }}
            />
          </Field>
          {(rules.pattern ?? '') !== '' && (
            <Field label="Что сказать, если не подошло">
              <Input
                value={rules.pattern_message ?? ''}
                placeholder="Артикул вида AB-123"
                onChange={(event) => {
                  set('pattern_message', event.target.value);
                }}
              />
            </Field>
          )}
        </>
      )}

      {allowed.has('unique') && (
        <Switch
          checked={rules.unique === true}
          onCheckedChange={(v) => {
            set('unique', v);
          }}
        >
          Не повторяется среди записей — артикул, адрес страницы
        </Switch>
      )}
    </>
  );
}

function ConditionEditor({
  condition,
  others,
  onChange,
}: {
  condition: Condition | undefined;
  others: FieldBody[];
  onChange: (condition: Condition | undefined) => void;
}) {
  const target = others.find((item) => item.key === condition?.field);
  const needsValue = condition?.op === 'equals' || condition?.op === 'not_equals';

  return (
    <div className={styles['condition']}>
      <Switch
        checked={condition !== undefined}
        disabled={others.length === 0}
        onCheckedChange={(on) => {
          const first = others[0];

          onChange(on && first !== undefined ? { field: first.key, op: 'filled' } : undefined);
        }}
      >
        Показывать только при условии
      </Switch>

      {others.length === 0 && (
        <p className={styles['muted']}>
          Условие ссылается на другое поле того же уровня — их пока нет.
        </p>
      )}

      {condition !== undefined && (
        <div className={styles['condition__row']}>
          <Select
            value={condition.field}
            onValueChange={(next) => {
              onChange({ ...condition, field: next, value: undefined });
            }}
          >
            {others.map((item) => (
              <SelectItem key={item.key} value={item.key}>
                {item.name}
              </SelectItem>
            ))}
          </Select>

          <Select
            value={condition.op}
            onValueChange={(next) => {
              onChange({ ...condition, op: next as ConditionOp });
            }}
          >
            {OPS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </Select>

          {needsValue && (
            <ConditionValue
              target={target}
              value={condition.value}
              onChange={(value) => {
                onChange({ ...condition, value });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Значение для сравнения — тем же способом, каким заполняется само поле. */
function ConditionValue({
  target,
  value,
  onChange,
}: {
  target: FieldBody | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (target?.type === 'select') {
    const list = parseChoices(choicesText(target));

    return (
      <Select
        value={typeof value === 'string' ? value : ''}
        onValueChange={onChange}
        placeholder="Вариант"
      >
        {list.map((choice) => (
          <SelectItem key={choice} value={choice}>
            {choice}
          </SelectItem>
        ))}
      </Select>
    );
  }

  if (target?.type === 'boolean') {
    return (
      <Segmented
        value={value === true ? 'yes' : 'no'}
        onValueChange={(next) => {
          onChange(next === 'yes');
        }}
        options={[
          { value: 'yes', label: 'включено' },
          { value: 'no', label: 'выключено' },
        ]}
      />
    );
  }

  return (
    <Input
      value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
      type={target?.type === 'number' ? 'number' : 'text'}
      placeholder="Значение"
      onChange={(event) => {
        const raw = event.target.value;

        onChange(target?.type === 'number' && raw !== '' ? Number(raw) : raw);
      }}
    />
  );
}

function choicesText(field: FieldBody | undefined): string {
  const raw = (field?.options as Record<string, unknown> | undefined)?.['choices'];

  return Array.isArray(raw) ? raw.filter((item) => typeof item === 'string').join('\n') : '';
}

function parseChoices(text: string): string[] {
  return [
    ...new Set(
      text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''),
    ),
  ];
}

/** Проверки, которых у вида нет, не отправляются: сервер их не примет. */
function pruneRules(rules: Rules, allowed: Set<string>): Rules {
  const out: Rules = {};

  if (allowed.has('length')) {
    if (rules.min_length !== undefined) out.min_length = rules.min_length;
    if (rules.max_length !== undefined) out.max_length = rules.max_length;
  }

  if (allowed.has('range')) {
    if (rules.min !== undefined) out.min = rules.min;
    if (rules.max !== undefined) out.max = rules.max;
  }

  if (allowed.has('integer') && rules.integer === true) out.integer = true;

  if (allowed.has('pattern') && (rules.pattern ?? '') !== '') {
    out.pattern = rules.pattern ?? '';

    if ((rules.pattern_message ?? '') !== '') out.pattern_message = rules.pattern_message ?? '';
  }

  if (allowed.has('unique') && rules.unique === true) out.unique = true;

  if (allowed.has('items')) {
    if (rules.min_items !== undefined) out.min_items = rules.min_items;
    if (rules.max_items !== undefined) out.max_items = rules.max_items;
  }

  return out;
}

/**
 * Предупреждение о последствиях: сколько записей и какие не подойдут.
 * Сохранить можно — это решение человека, — но вслепую нельзя.
 */
function ImpactWarning({ impact }: { impact: ImpactOutputBody }) {
  return (
    <div className={styles['impact']} role="alert">
      {impact.failing > 0 && (
        <p>
          <b>
            Не подойдут {impact.failing} из {impact.records} записей.
          </b>{' '}
          Их нельзя будет опубликовать, пока не поправите.
        </p>
      )}
      {impact.duplicates > 0 && (
        <p>
          <b>Повторяются значения у {impact.duplicates} записей</b> — уникальность не даст сохранить
          их, пока повторы не разведут.
        </p>
      )}
      {(impact.examples ?? []).length > 0 && (
        <ul className={styles['impact__list']}>
          {(impact.examples ?? []).map((example) => (
            <li key={example.record_id}>
              «{example.title === '' ? 'без названия' : example.title}» — {example.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Какие блоки можно ставить в это поле: у «Статьи» — текст и картинки, у
 * «Страницы» — ещё Hero и FAQ. Ничего не отмечено — любые.
 */
function AllowedBlocks({
  chosen,
  onChange,
}: {
  chosen: string[];
  onChange: (next: string[]) => void;
}) {
  const scope = useSiteScope();
  const env = kinds[scope?.env ?? 'prod'];
  const siteID = scope?.siteId ?? '';

  const blocks = useQuery({
    queryKey: ['block-types', siteID, env],
    enabled: siteID !== '',
    queryFn: async () => {
      const response = await contentBlockTypes(siteID, env);

      return response.status === 200 ? (response.data.items ?? []) : [];
    },
  });

  return (
    <Field label="Какие блоки можно ставить" hint="Ничего не отмечено — любые.">
      <div className={styles['blocks']}>
        {(blocks.data ?? []).map((block) => (
          <Checkbox
            key={block.type}
            checked={chosen.includes(block.type)}
            onCheckedChange={(on) => {
              onChange(
                on === true
                  ? [...chosen, block.type]
                  : chosen.filter((item) => item !== block.type),
              );
            }}
          >
            {block.title}
            {block.type_id != null ? ' · свой' : ''}
          </Checkbox>
        ))}
      </div>
    </Field>
  );
}
